import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Dialog } from "@headlessui/react";
import { MdClose, MdCheckCircle, MdCancelPresentation } from "react-icons/md";
import { useInView } from "react-intersection-observer";
import { Input, Select } from "../ui";
import Button from "../ui/Button";
import InlineLoader from "../loader/InlineLoader";
import { useRegularizationHistory } from "../../hooks/useRegularizationHistory";
import { LuCalendarSync } from "react-icons/lu";

const attendanceStatusOptions = [
  { value: "present", label: "Present" },
  { value: "absent", label: "Absent" },
  { value: "half_day", label: "Half day" },
  { value: "holiday", label: "Holiday" },
  { value: "weekend_full_day", label: "Weekend full day" },
  { value: "weekend_first_half", label: "Weekend first half" },
  { value: "weekend_second_half", label: "Weekend second half" },
  { value: "on_approved_leave", label: "On approved leave" },
  { value: "missed_punch", label: "Missed Punch" },
  { value: "weekend", label: "Weekend" },
];

// Statuses that don't require time fields
const NO_TIME_STATUSES = [
  "absent",
  "weekend_full_day",
  "weekend",
  "on_approved_leave",
  "holiday",
];

// Helper function to check if status requires time fields
const statusRequiresTimeFields = (status) => !NO_TIME_STATUSES.includes(status);

const RegularizationModal = ({
  isOpen,
  onClose,
  attendanceData,
  onSubmit,
  isSubmitting,
  activeTab,
  onTabChange,
}) => {
  const { ref: loadMoreRef, inView } = useInView();

  const {
    historyList,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingHistory,
  } = useRegularizationHistory(
    attendanceData?.id,
    isOpen && activeTab === "history"
  );

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Determine current status based on regularization flag
  const getCurrentStatus = () => {
    if (attendanceData?.regularization === true) {
      return attendanceData?.payment_definition;
    } else if (attendanceData?.regularization === false) {
      return attendanceData?.present_definition;
    }
  };

  // Check if the latest request (first in history) is pending
  // Otherwise, fall back to the attendanceData prop
  const hasPending = historyList.length > 0 && historyList[0].status === "pending"
    ? true
    : attendanceData?.pending_regularization_request;

  const { control, handleSubmit, reset, watch } = useForm({
    mode: "onSubmit",
    defaultValues: {
      current_status: getCurrentStatus() || "",
      current_in_time: attendanceData?.in_time || "",
      current_out_time: attendanceData?.out_time || "",
      requested_status: "",
      requested_in_time: attendanceData?.in_time || "",
      requested_out_time: attendanceData?.out_time || "",
      request_reason: "",
    },
  });

  const currentStatus = watch("current_status");
  const requestedStatus = watch("requested_status");

  // Filter out the current status from requested status options
  const filteredStatusOptions = attendanceStatusOptions.filter(
    (option) => option.value !== currentStatus
  );

  const handleFormSubmit = (data) => {
    const payload = {
      attendance_id: attendanceData.id,
      current_status: data.current_status,
      current_in_time: attendanceData.in_time,
      current_out_time: attendanceData.out_time,
      requested_status: data.requested_status,
      requested_in_time: statusRequiresTimeFields(data.requested_status)
        ? data.requested_in_time
        : "",
      requested_out_time: statusRequiresTimeFields(data.requested_status)
        ? data.requested_out_time
        : "",
      request_reason: data.request_reason,
    };
    onSubmit(payload);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return "--";
    return timeString.substring(0, 5);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="fixed inset-0 z-[1002] overflow-y-auto"
    >
      <div className="flex min-h-screen items-center justify-center p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-50" />

        <div className="relative bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between p-4 border-b">
            <Dialog.Title className="text-lg font-semibold flex items-center">
              <LuCalendarSync className="w-5 h-5 mr-2" />
              Attendance Regularization
            </Dialog.Title>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <MdClose className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b bg-gray-50">
            <button
              onClick={() => onTabChange("request")}
              className={`px-4 py-3 font-medium transition-colors ${activeTab === "request"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              Request Regularization
            </button>
            <button
              onClick={() => onTabChange("history")}
              className={`px-4 py-3 font-medium transition-colors ${activeTab === "history"
                  ? "border-b-2 border-blue-600 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              History
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Request Tab */}
            {activeTab === "request" && (
              <form
                onSubmit={handleSubmit(handleFormSubmit)}
                className="p-6 space-y-4 min-h-[450px] flex flex-col"
              >
                {hasPending && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-yellow-800 font-medium">
                      You already have a pending regularization request for
                      this attendance. Please check the History tab to view the
                      status of your existing request.
                    </p>
                  </div>
                )}

                <Select
                  control={control}
                  name="current_status"
                  label="Current Status"
                  options={attendanceStatusOptions}
                  rules={{ required: "Current status is required" }}
                  disabled
                />

                {(attendanceData?.in_time || attendanceData?.out_time) && (
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      control={control}
                      name="current_in_time"
                      label="Current In Time"
                      type="time"
                      disabled
                    />
                    <Input
                      control={control}
                      name="current_out_time"
                      label="Current Out Time"
                      type="time"
                      disabled
                    />
                  </div>
                )}

                <Select
                  control={control}
                  name="requested_status"
                  label="Requested Status"
                  options={filteredStatusOptions}
                  rules={{ required: "Requested status is required" }}
                  disabled={hasPending}
                />

                {statusRequiresTimeFields(requestedStatus) && (
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      control={control}
                      name="requested_in_time"
                      label="Requested In Time"
                      type="time"
                      rules={{ required: "In time is required" }}
                      disabled={hasPending}
                    />
                    <Input
                      control={control}
                      name="requested_out_time"
                      label="Requested Out Time"
                      type="time"
                      rules={{ required: "Out time is required" }}
                      disabled={hasPending}
                    />
                  </div>
                )}

                <Input
                  control={control}
                  name="request_reason"
                  label="Reason for Request"
                  isTextarea
                  rules={{ required: "Reason is required" }}
                  placeholder="Please provide a reason for this regularization request"
                  disabled={hasPending}
                />

                <div className="flex justify-end space-x-3 pt-4 mt-auto">
                  <Button type="button" variant="outline" onClick={handleClose}>
                    Close
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    disabled={isSubmitting || hasPending}
                  >
                    {isSubmitting ? (
                      <>
                        <InlineLoader className="text-white mr-2" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </form>
            )}

            {/* History Tab */}
            {activeTab === "history" && (
              <div className="p-6 min-h-[450px]">
                {isLoadingHistory ? (
                  <div className="flex items-center justify-center">
                    <InlineLoader className="text-blue-600" />
                  </div>
                ) : historyList.length > 0 ? (
                  <div className="space-y-4">
                    {historyList.map((request, idx) => (
                      <div
                        key={idx}
                        className="border rounded-lg overflow-hidden shadow-sm"
                      >
                        {/* Header with Status and Request Date */}
                        <div
                          className={`px-4 py-3 flex items-center justify-between ${request.status === "approved"
                              ? "bg-green-50"
                              : request.status === "rejected"
                                ? "bg-red-50"
                                : "bg-yellow-50"
                            }`}
                        >
                          <div className="flex items-center space-x-2">
                            {request.status === "approved" && (
                              <MdCheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            {request.status === "rejected" && (
                              <MdCancelPresentation className="w-5 h-5 text-red-600" />
                            )}
                            <div>
                              <p
                                className={`font-semibold text-sm ${request.status === "approved"
                                    ? "text-green-900"
                                    : request.status === "rejected"
                                      ? "text-red-900"
                                      : "text-yellow-900"
                                  }`}
                              >
                                {request.status === "pending"
                                  ? "Pending Review"
                                  : request.status === "approved"
                                    ? "Approved"
                                    : "Rejected"}
                              </p>
                              <p className="text-xs opacity-75">
                                Requested on {formatDate(request.created_at)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="px-4 py-4 bg-white space-y-4">
                          {/* Current vs Requested Status - Side by Side */}
                          <div className="grid grid-cols-2 gap-4">
                            <div className="bg-gray-50 p-3 rounded">
                              <p className="text-xs text-gray-600 font-medium mb-1">
                                Current Status
                              </p>
                              <p className="font-semibold text-sm capitalize">
                                {request.current_status?.replace(/_/g, " ")}
                              </p>
                            </div>
                            <div className="bg-blue-50 p-3 rounded border border-blue-200">
                              <p className="text-xs text-blue-600 font-medium mb-1">
                                Requested Status
                              </p>
                              <p className="font-semibold text-sm capitalize text-blue-900">
                                {request.requested_status?.replace(/_/g, " ")}
                              </p>
                            </div>
                          </div>

                          {/* Times */}
                          {(request.requested_in_time ||
                            request.requested_out_time ||
                            request.current_in_time ||
                            request.current_out_time) && (
                            <div>
                              <p className="text-xs text-gray-600 font-medium mb-2">
                                Work Hours
                              </p>
                              <div className="grid grid-cols-2 gap-4">
                                {/* Current Hours */}
                                <div className="bg-gray-50 p-3 rounded">
                                  <p className="text-xs text-gray-500 mb-2">Current Hours</p>
                                  <div className="space-y-2">
                                    <div>
                                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Punch In</p>
                                      <p className="font-mono font-medium text-sm">
                                        {formatTime(request.current_in_time)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1">Punch Out</p>
                                      <p className="font-mono font-medium text-sm">
                                        {formatTime(request.current_out_time)}
                                      </p>
                                    </div>
                                  </div>
                                </div>

                                {/* Requested Hours */}
                                <div className="bg-blue-50 p-3 rounded border border-blue-200">
                                  <p className="text-xs text-blue-600 font-medium mb-2">Requested Hours</p>
                                  <div className="space-y-2">
                                    <div>
                                      <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Punch In</p>
                                      <p className="font-mono font-semibold text-sm text-blue-900">
                                        {formatTime(request.requested_in_time)}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] text-blue-400 uppercase tracking-wider mb-1">Punch Out</p>
                                      <p className="font-mono font-semibold text-sm text-blue-900">
                                        {formatTime(request.requested_out_time)}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Reason */}
                          <div>
                            <p className="text-xs text-gray-600 font-medium mb-1">
                              Reason
                            </p>
                            <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
                              {request.request_reason || "--"}
                            </p>
                          </div>

                          {/* Status-specific Details */}
                          {request.status === "approved" && (
                            <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded">
                              <p className="text-xs text-green-700">
                                Approved by{" "}
                                {request.approved_by_name || "Admin"} on{" "}
                                {formatDate(request.approved_at)}
                              </p>
                            </div>
                          )}

                          {request.status === "rejected" && (
                            <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded">
                              <p className="text-xs text-red-700 mb-2">
                                Rejected by{" "}
                                {request.rejected_by_name || "Admin"} on{" "}
                                {formatDate(request.rejected_at)}
                              </p>
                              {request.reject_reason && (
                                <div className="bg-white p-2 rounded text-xs text-red-800 border border-red-200">
                                  <span className="font-medium">Reason:</span>{" "}
                                  {request.reject_reason}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {/* Infinite scroll sentinel */}
                    <div ref={loadMoreRef} className="py-4 text-center">
                      {isFetchingNextPage && (
                        <InlineLoader className="text-blue-600" />
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <p className="text-gray-500">
                      No regularization requests found
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default RegularizationModal;
